<template>
    <guest-invoices-setup-base-card v-if="reservation">
        <template v-slot:card-body>
            <b-row>
                <b-col>
                    <b>ENTERED DATE:</b>
                </b-col>
                <b-col cols="auto">
                    <span v-if="!reservationEditEntered">
                        {{ enteredDateDisplayValue }}
                    </span>
                    <form-datepicker
                        v-click-outside="handleOutsideClick"
                        v-else-if="enteredDate.isEdit && selectedCell === 'enteredDate' && reservationEditEntered"
                        ref="enteredDate"
                        :no-gutters="true"
                        :hide-header="true"
                        v-model="reservation.created_edit"
                        :display-format="userUnicodeDateFormat"
                        @input="handleEnteredDateChange"
                    ></form-datepicker>
                    <span
                        v-else
                        type="button"
                        class="text-primary guest-invoices-setup-base-card__clickable-cell"
                        @click="editCellHandler('enteredDate')"
                    >
                        {{ enteredDateDisplayValue }}
                    </span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>ENTERED BY:</b>
                </b-col>
                <b-col cols="auto">
                    <span v-if="!reservationEditEntered">
                        {{ currentEnteredBy }}
                    </span>
                    <form-select
                        v-click-outside="handleOutsideClick"
                        ref="enteredBy"
                        :autofocus="true"
                        v-model="reservation.creator"
                        :options="metadata.creator_choices"
                        @input="handleEnteredByChange"
                        v-else-if="enteredBy.isEdit && selectedCell === 'enteredBy' && reservationEditEntered"
                    />
                    <span
                        v-else
                        type="button"
                        class="text-primary guest-invoices-setup-base-card__clickable-cell"
                        @click="editCellHandler('enteredBy')"
                    >
                        {{ currentEnteredBy }}
                    </span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>ADULTS:</b>
                </b-col>
                <b-col class="text-right">
                    <span class="mr-1">{{ reservation.guests }}</span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>CHILDREN:</b>
                </b-col>
                <b-col class="text-right">
                    <span class="mr-1">{{ reservation.children }}</span>
                </b-col>
            </b-row>
            <b-row v-if="reservation.pets">
                <b-col>
                    <b>PETS:</b>
                </b-col>
                <b-col class="text-right">
                    <span class="mr-1">{{ reservation.pets }}</span>
                </b-col>
            </b-row>
        </template>
    </guest-invoices-setup-base-card>
</template>

<script>
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import GuestInvoicesSetupBaseCard from './base-card.vue';
import FormDatepicker from '../../../../generic/form-datepicker/form-datepicker.vue';
import { GUEST_RESERVATION_ENDPOINT } from '../../../../../endpoints';
import { formatResponseError } from '../../../../../utils/api';
import FormSelect from '../../../../generic/form-select/form-select.vue';

export default {
    name: 'GuestInvoicesSetupFirstCard',
    components: { GuestInvoicesSetupBaseCard, FormDatepicker, FormSelect },
    computed: {
        ...mapGetters('guest', ['metadata', 'reservation']),
        ...mapGetters('header', ['userUnicodeDateFormat']),
        guestId() {
            return parseInt(this.$route.params.id);
        },
        reservationId() {
            return parseInt(this.$route.query.reservation);
        },
        reservationEditEntered() {
            return this.metadata?.permissions?.reservation_edit_entered;
        },
        currentEnteredBy() {
            return this.metadata.creator_choices.find((r) => r.value == this.reservation.creator)?.text || 'Guest';
        },
        enteredDateDisplayValue() {
            if (this.reservation.created_edit)
                return format(parseISO(this.reservation.created_edit), this.userUnicodeDateFormat);
            return '';
        },
    },
    data() {
        return {
            selectedCell: null,
            enteredDate: { previous: null, isEdit: false },
            enteredBy: { previous: null, isEdit: false },
            createdDateFormat: "yyyy-MM-dd'T'12:00:00",
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    methods: {
        handleOutsideClick(event) {
            this.enteredDate.isEdit = false;
            this.enteredBy.isEdit = false;
            this.selectedCell = null;
        },
        editCellHandler(name) {
            this[name].isEdit = true;
            this.selectedCell = name;
            if (name == 'enteredDate') this.showEnteredDate();
            if (name == 'enteredBy') this.enteredBy.previous = this.reservation.creator;
        },
        showEnteredDate() {
            this.enteredDate.previous = this.reservation.created_edit;
            this.$nextTick(() => this.$refs.enteredDate.show());
        },
        formatCreatedDate(date) {
            return format(parseISO(date), this.createdDateFormat);
        },
        async handleEnteredDateChange(value) {
            const previous = this.formatCreatedDate(this.enteredDate.previous);
            const newCreated = this.formatCreatedDate(value);
            try {
                this.$emit('loading', true);
                if (newCreated != previous) {
                    await API.patch(GUEST_RESERVATION_ENDPOINT(this.reservationId, this.guestId), {
                        created: newCreated,
                    });
                    this.showSuccessToast({
                        text: 'Entered date successfully updated.',
                    });
                }
            } catch (e) {
                this.reservation.created_edit = previous;
                const { generic } = formatResponseError(e);
                this.showErrorToast({
                    text: generic || 'Could not change entered date.',
                });
            } finally {
                this.$emit('loading', false);
                this.handleOutsideClick();
            }
        },
        async handleEnteredByChange(value) {
            const previous = this.enteredBy.previous;
            const newCreator = value;
            try {
                this.$emit('loading', true);
                if (newCreator != previous) {
                    await API.patch(GUEST_RESERVATION_ENDPOINT(this.reservationId, this.guestId), {
                        creator: newCreator,
                    });
                    this.showSuccessToast({
                        text: 'Entered by successfully updated.',
                    });
                }
            } catch (e) {
                this.reservation.creator = previous;
                const { generic } = formatResponseError(e);
                this.showErrorToast({
                    text: generic || 'Could not change entered by.',
                });
            } finally {
                this.$emit('loading', false);
                this.handleOutsideClick();
            }
        },
    },
};
</script>
